<script setup lang="ts">
import { cardParticles, hexAlpha, mixColor } from '@/utils/levels';

// Faixa de nível do jogo no DESKTOP (Figma 22582:64412). Mesmo conteúdo da
// versão mobile — [emblema] · [Elo: lv N] · [barra] · [NN%] —, mas NÃO é a
// mesma faixa com breakpoints: aqui é uma PLACA solta, com moldura e fundo
// próprios, e nenhuma medida bate com a do mobile (texto 20px contra 11,2px,
// trilha de 26px contra 10px, emblema de 65px contra 19px). Por isso mora num
// arquivo separado, e não num `lg:` do LevelsInGameProgress.
//
// O fundo é TINGIDO pelo elo: no Figma o gradiente vai de #3D146D a #080923, e
// #3D146D é exatamente o violeta da Ametista afogado em 66% do #080923 — então
// a mesma conta serve pra qualquer elo, sem uma tabela paralela de fundos.
//
// AQUI a faixa É clicável e leva pra página de níveis — ao contrário da versão
// mobile, que é só leitura de propósito: lá ela mora a um dedo do topo do jogo e
// um toque errado tirava o jogador da partida. No desktop o alvo é o cursor, o
// clique é deliberado, e a placa fica acima do frame — então ela é um link.
const { tier, level, levelProgress, currentLevelLabel } = useLevelsProgress();

const progressLabel = computed(() => Math.round(levelProgress.value));

// As mesmas partículas da pele dos elos (LevelsTierSkin / cartão do jogador):
// aqui elas entram SOLTAS, sem a pele inteira, porque o desenho desta faixa pede
// a moldura chapada e o fundo próprio dele — o que se aproveita é só a poeira
// pulsando atrás do conteúdo.
const particles = computed(() => cardParticles(tier.value));

const CARD_BASE = '#080923';

// A moldura é um GRADIENTE que corre com a barra: claro onde o XP começa, escuro
// onde ele vai chegar. O `get_design_context` do Figma achatou o traço num
// `#8282ff` sozinho, mas o painel de bordas do arquivo declara `Linear Gradient`
// #8282FF → #4800BC, e o export 4x confirma (o traço lê (125,120,249) na
// esquerda e caminha pro (72,0,188) na direita).
//
// O par NÃO sai daqueles dois hex: eles são a cor do elo Ametista já resolvida,
// e usá-los como base carregaria o roxo dela pra dentro dos outros 13 elos. O
// que se herda do desenho é a RELAÇÃO — o começo com algumas vezes a luminância
// do fim — e é a `tier.color` que gera as duas pontas. Na Ametista isso devolve
// #BF6BFF → #411166 (4,1x), na amplitude do #8282FF → #4800BC do Figma (4,8x).
const BORDER_LIGHTEN = 0.3;
const BORDER_DARKEN = 0.6;
</script>

<template>
  <!-- Duas camadas de fundo em vez de `border-color`: `border-color` não aceita
       gradiente. O truque é a borda transparente com o fundo do card recortado
       no `padding-box` e o gradiente do traço no `border-box` — assim o traço
       aparece só na moldura de 1px, sem elemento extra nem wrapper de padding. -->
  <NuxtLink
    :aria-label="`${currentLevelLabel} — ${$t('casino.game.levels')}`"
    class="levels-ingame-desktop relative block h-[73px] w-full select-none rounded-[12px] border border-solid border-transparent px-[6.5px] hover:brightness-125"
    :draggable="false"
    no-prefetch
    :style="{
      background: `linear-gradient(180deg,${mixColor(tier.color, CARD_BASE, 0.66)},${CARD_BASE}) padding-box,linear-gradient(90deg,${mixColor(tier.color, '#ffffff', BORDER_LIGHTEN)},${mixColor(tier.color, '#000000', BORDER_DARKEN)}) border-box`,
    }"
    :to="localeLink('/vip/levels')"
  >
    <!--
      Poeira do elo atrás do conteúdo. `overflow: clip` e não `hidden`: `hidden`
      cria contexto de rolagem, e a faixa mora a um dedo do frame do jogo.
      O raio é o interno (12 menos a borda de 1px), senão as partículas de canto
      aparecem por baixo da moldura.
    -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 rounded-[11px] [overflow:clip]"
    >
      <span
        v-for="(particle, index) in particles"
        :key="index"
        class="levels-ingame-pop absolute rounded-full"
        :style="{
          left: particle.left,
          top: particle.top,
          width: particle.size,
          height: particle.size,
          background: particle.color,
          boxShadow: `0 0 ${particle.blur} ${particle.color}`,
          animationDuration: particle.duration,
          animationDelay: particle.delay,
        }"
      ></span>
    </div>

    <!-- Conteúdo `relative` porque a camada das partículas é posicionada: sem
         isso ela pintaria por cima do texto e do emblema. -->
    <div class="relative flex h-full items-center gap-[15px]">
      <!-- Bloco do elo: caixa de 228px com o emblema e o rótulo nas pontas
           (`justify-between` do desenho). `shrink-0` porque quem cede largura é
           a trilha. -->
      <div class="flex w-[228px] shrink-0 items-center justify-between">
        <!-- Emblema RECORTADO, não encaixado: a arte é escalada a 80,83px
             (108,3% da largura da janela, como no desenho) e a janela de
             74,638 × 65,208 corta o que passa. Com `bg-contain` o brasão saía
             visivelmente menor que o do Figma, porque a arte é quadrada e a
             janela é mais baixa que larga.
             O recorte é CENTRADO, e não puxado 8,19px pra cima como o Figma
             pede: aquele valor foi desenhado sobre a Ametista, cuja arte é um
             losango; nas 7 faixas VIP, que são coroas assentadas mais embaixo
             na tela quadrada, ele decepava a base da coroa. Centrado, os 15,6px
             de sobra saem metade de cada lado e as duas famílias de arte
             sobrevivem. -->
        <span
          aria-hidden="true"
          class="h-[65.208px] w-[74.638px] flex-none bg-center bg-no-repeat [overflow:clip]"
          :style="{
            backgroundImage: `url(${tier.badge})`,
            backgroundSize: '80.83px 80.83px',
            filter: 'drop-shadow(0 2.426px 8.491px rgba(0,0,0,.55))',
          }"
        ></span>

        <span
          class="w-[150px] text-[20px] font-semibold leading-none text-white/60"
        >
          <b class="font-bold text-white">{{ tier.name }}:</b>
          lv {{ level }}
        </span>
      </div>

      <!-- Trilha: `flex items-center` em vez de preenchimento absoluto, porque o
           desenho centra uma barra de 24,89px dentro dos 26px da calha — com o
           filho posicionado no fluxo os 0,55px de folga saem de graça. -->
      <span
        class="levels-ingame-track flex h-[26px] min-w-0 flex-1 items-center overflow-hidden rounded-full shadow-[inset_0_1.973px_3.946px_rgba(0,0,0,0.5)]"
      >
        <span
          class="block h-[24.89px] rounded-full"
          :style="{
            width: `${levelProgress}%`,
            background: `linear-gradient(90deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.42)})`,
            boxShadow: `0 0 15.782px ${hexAlpha(tier.color, 0.9)}`,
          }"
        ></span>
      </span>

      <span
        class="w-[56px] shrink-0 text-[20px] font-bold leading-none text-white tabular-nums"
      >
        {{ progressLabel }}%
      </span>
    </div>
  </NuxtLink>
</template>

<style scoped lang="postcss">
/* Clicar e arrastar na placa não seleciona nada nem sai carregando fantasma.
   Quem faz o trabalho aqui é o `draggable="false"` do template: um <a> é
   arrastável por padrão, e sem ele o Chromium disparava um `dragstart` e o
   cursor saía com o fantasma do atalho (medido: 1 evento antes, 0 depois).
   O `-webkit-user-drag` cobre o WebKit, que ignora o atributo em alguns casos.

   O `select-none` do template é cinto e suspensório: no Chromium o texto dentro
   de um link já não seleciona por arraste nem por duplo-clique (conferido), mas
   Firefox e Safari selecionam, e a proteção sobrevive se a placa algum dia
   deixar de ser um link.

   O emblema não precisa de tratamento próprio — é `background-image`, e fundo
   não se arrasta; se um dia virar <img>, aí sim ele vira alvo de arraste. */
.levels-ingame-desktop {
  -webkit-user-drag: none;
}

/* Calha vazia: o Figma pede o azul-marinho chapado `rgba(0,25,101,.74)`, mas
   correndo no sentido da barra ela ganha profundidade — escura onde o XP começa,
   clara onde ele vai chegar. A ponta direita para em 68% de opacidade de
   propósito: mais clara que isso e o vazio começa a competir com o cheio nos
   elos claros (Diamante, Prata). O azul NÃO é tingido pelo elo — é ele que
   sustenta o contraste com a barra colorida. */
.levels-ingame-track {
  background: linear-gradient(
    90deg,
    rgba(0, 10, 42, 0.86),
    rgba(0, 44, 138, 0.68)
  );
}

/* O reset global mata `transition`, então a pulsação anda por keyframes. Mesma
   curva da pele dos elos (LevelsTierSkin), pra a poeira não pulsar de dois
   jeitos diferentes na mesma sessão. */
@keyframes levelsIngamePop {
  0%,
  100% {
    opacity: 0;
    transform: translateY(4px) scale(0.6);
  }
  45% {
    opacity: 0.9;
    transform: translateY(-4px) scale(1);
  }
}

.levels-ingame-pop {
  opacity: 0;
  animation-name: levelsIngamePop;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
</style>
